import React, { useState, useMemo, useCallback } from "react";

// @material-ui/core
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Checkbox from "@material-ui/core/Checkbox";
import ListItemText from "@material-ui/core/ListItemText";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";

// core components
import GridItem from "components/Grid/GridItem.js";
import GridContainer from "components/Grid/GridContainer";
import Table from "components/Tables/StatTable.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";
import {StatHeaders, StatNutroscanHeaders, convertDataIntoTableData,} from "../../utils/table.js";
import StatisticsReport from "components/StatisticsReport/StatisticsReport";
import RecalculateReadingsModal from "components/RecalculateReadingsModal/RecalculateReadingsModal";

//@ts-ignore
import lodash from "lodash";

import { ChartData } from "types/chartData";

import StatisticsHandler from "../../statisticsModels/StatisticsHandler.js";
import SpinLoading from "components/Loading/SpinLoading.js";
import intl from "react-intl-universal";

import Reading from "spectral-core/models/Reading";

import styles from "assets/jss/material-dashboard-react/views/dashboardStyle.js";
import { useRootSelector } from "hooks/reduxHooks";

import { useUserLoggedIn } from "hooks/userHooks";
import { useGetReadings } from "hooks/readingsHooks";
import Chart from "components/Chart/Chart";

//@ts-ignore
const useStyles = makeStyles(styles);

function createChartData(readings: Reading[]) {
  if (readings.length === 0) {
    return undefined;
  }

  //@ts-ignore
  let predicteds = lodash.map(readings, (item) => {
    return item != null && item.predictedValues != null
      ? JSON.parse(item.predictedValues)
      : [];
  });

  predicteds = lodash.flattenDeep(
    //@ts-ignore
    predicteds.filter((predicted) => predicted != null)
  );

  if (predicteds.length === 0) return undefined;

  const statisticsHandler = new StatisticsHandler(predicteds);
  return statisticsHandler.createAnalysis() as ChartData;
}

//@ts-ignore
export default function Statistics() {
  const classes = useStyles();

  const userAccessLevel = useRootSelector(
    (state) => state.user.currentUser?.accessLevel
  );

  const userCompany = useRootSelector(
    (state) => state.user.currentUser?.company
  );

  const canGenerateReports =
  userCompany === "ICL" || userAccessLevel === 9000;

  const accessToken = useRootSelector(
    (state) => state.user.currentToken?.accessToken
  );

  const tableHeaders = StatHeaders;

  const [selectedReadings, setSelectedReadings] = useState<Reading[]>([]);

  const hasDifferentReadingCultures =  selectedReadings.length > 1 && 
  new Set(
    selectedReadings
      .map((r) => r.culture)
      .filter(Boolean)
  ).size > 1;

  const isLoggedIn = useUserLoggedIn();

  const { isFetching } = useGetReadings();

  const readings = useRootSelector((state) => state.reading.readings);

  const chartData = useMemo(
    () => createChartData(selectedReadings),
    [selectedReadings]
  );

  const loading = !isLoggedIn || readings === undefined;
  const actionsDisabled = loading || isFetching;

  // Filtros globais — mesmo formato da página NutroScan da dash ICL
  const [filterUser, setFilterUser] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<string>("");
  const [filterName, setFilterName] = useState<string[]>([]);
  const [filterSerial, setFilterSerial] = useState<string>("");
  const [filterDateFrom, setFilterDateFrom] = useState<string>("");
  const [filterDateTo, setFilterDateTo] = useState<string>("");

  const baseReadings = useMemo(
    () => (readings !== undefined ? readings : []),
    [readings]
  );

  const availableUsers = useMemo(() => {
    const set = new Set<string>();
    for (const r of baseReadings) {
      const user = (r.userName || "").trim();
      if (user) set.add(user);
    }
    return Array.from(set).sort();
  }, [baseReadings]);

  // Filtro em cascata: cada lista respeita os filtros anteriores
  const afterUser = useMemo(() => {
    if (filterUser.length === 0) return baseReadings;
    return baseReadings.filter((r) => filterUser.includes((r.userName || "").trim()));
  }, [baseReadings, filterUser]);

  const availableTypes = useMemo(() => {
    const set = new Set<string>();
    for (const r of afterUser) {
      const t = (r.type || "").trim();
      if (t) set.add(t);
    }
    return Array.from(set).sort();
  }, [afterUser]);

  const afterType = useMemo(() => {
    if (!filterType) return afterUser;
    return afterUser.filter((r) => (r.type || "").trim() === filterType);
  }, [afterUser, filterType]);

  const availableNames = useMemo(() => {
    const set = new Set<string>();
    for (const r of afterType) {
      const n = (r.name || "").trim();
      if (n) set.add(n);
    }
    return Array.from(set).sort();
  }, [afterType]);

  const availableSerials = useMemo(() => {
    const set = new Set<string>();
    for (const r of afterType) {
      const s = (r.serialNumber || "").trim();
      if (s) set.add(s);
    }
    return Array.from(set).sort();
  }, [afterType]);

  const filteredReadings = useMemo(() => {
    let filtered = afterType;
    if (filterName.length > 0) {
      filtered = filtered.filter((r) => filterName.includes((r.name || "").trim()));
    }
    if (filterSerial) {
      filtered = filtered.filter((r) => (r.serialNumber || "").trim() === filterSerial);
    }
    if (filterDateFrom) {
      const from = new Date(filterDateFrom + "T00:00:00");
      filtered = filtered.filter((r) => {
        const dt = r.dateTime;
        if (!dt) return false;
        const [d, m, y] = dt.split(" ")[0].split("/").map(Number);
        return new Date(y, m - 1, d) >= from;
      });
    }
    if (filterDateTo) {
      const to = new Date(filterDateTo + "T23:59:59");
      filtered = filtered.filter((r) => {
        const dt = r.dateTime;
        if (!dt) return false;
        const [d, m, y] = dt.split(" ")[0].split("/").map(Number);
        return new Date(y, m - 1, d) <= to;
      });
    }
    return filtered;
  }, [afterType, filterName, filterSerial, filterDateFrom, filterDateTo]);

  const tableData = useMemo(
    () => convertDataIntoTableData(filteredReadings),
    [filteredReadings]
  );

  const handleSelectionCallback = useCallback(
    (rows: any[]) => {
      const selected = rows
        .map((row) => filteredReadings[row.tableData.index])
        .filter(Boolean);
      setSelectedReadings(selected);
    },
    [filteredReadings]
  );

  const table = useMemo(() => {
    return loading ? (
      <SpinLoading />
    ) : (
      <Table
        headers={tableHeaders}
        tableData={tableData}
        SelectionChange={handleSelectionCallback}
      />
    );
  }, [loading, handleSelectionCallback, tableData]);

  const [recalculateModalOpen, setRecalculateModalOpen] = useState(false);

  const handleOpenRecalculateModal = () => {
    if (hasDifferentReadingCultures) {
      alert(intl.get("alerts.differentCulturesError"));
      return;
    }

    setRecalculateModalOpen(true);
  };

  return (
    <div>
      <GridContainer alignItems="center" spacing={2}>
        <GridItem>
          <StatisticsReport
            selectedReadings={selectedReadings}
            statisticsData={chartData}
            isFetching={isFetching}
          />
        </GridItem>

        <GridItem>
          {canGenerateReports && (
            <div style={{ display: "flex", gap: 32 }}>
              <button
                className="reportButtom runModelButton"
                onClick={handleOpenRecalculateModal}
                disabled={actionsDisabled || selectedReadings.length === 0}
              >
                {isFetching ? intl.get("btn.loading") : intl.get("btn.recalculate")}
              </button>
            </div>
          )}
        </GridItem>
      </GridContainer>
      {/* Filtros globais — mesmo formato da página NutroScan da dash ICL */}
      <Paper style={{ padding: "12px 16px", marginTop: 12, marginBottom: 12, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <FormControl style={{ minWidth: 160, flex: "1 1 160px" }} size="small">
          <InputLabel id="filter-user" shrink>Usuário</InputLabel>
          <Select
            labelId="filter-user"
            multiple
            value={filterUser}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
              setFilterUser(e.target.value as string[]);
              setFilterType("");
              setFilterName([]);
              setFilterSerial("");
            }}
            displayEmpty
            renderValue={(selected) => {
              const sel = selected as string[];
              if (sel.length === 0) return "Todos";
              if (sel.length === 1) return sel[0];
              return `${sel.length} usuários`;
            }}
          >
            {availableUsers.map((u) => (
              <MenuItem key={u} value={u}>
                <Checkbox checked={filterUser.indexOf(u) > -1} size="small" color="primary" />
                <ListItemText primary={u} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl style={{ minWidth: 150, flex: "1 1 150px" }} size="small">
          <InputLabel id="filter-type" shrink>Aplicação</InputLabel>
          <Select
            labelId="filter-type"
            value={filterType}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) => {
              setFilterType(e.target.value as string);
              setFilterName([]);
              setFilterSerial("");
            }}
            displayEmpty
          >
            <MenuItem value="">Todas</MenuItem>
            {availableTypes.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl style={{ minWidth: 160, flex: "1 1 160px" }} size="small">
          <InputLabel id="filter-name" shrink>Nome da amostra</InputLabel>
          <Select
            labelId="filter-name"
            multiple
            value={filterName}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
              setFilterName(e.target.value as string[])
            }
            displayEmpty
            renderValue={(selected) => {
              const sel = selected as string[];
              if (sel.length === 0) return "Todas";
              if (sel.length === 1) return sel[0];
              return `${sel.length} nomes`;
            }}
          >
            {availableNames.map((n) => (
              <MenuItem key={n} value={n}>
                <Checkbox checked={filterName.indexOf(n) > -1} size="small" color="primary" />
                <ListItemText primary={n} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl style={{ minWidth: 150, flex: "1 1 150px" }} size="small">
          <InputLabel id="filter-serial" shrink>Serial Number</InputLabel>
          <Select
            labelId="filter-serial"
            value={filterSerial}
            onChange={(e: React.ChangeEvent<{ value: unknown }>) =>
              setFilterSerial(e.target.value as string)
            }
            displayEmpty
          >
            <MenuItem value="">Todos</MenuItem>
            {availableSerials.map((s) => (
              <MenuItem key={s} value={s}>{s}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="De"
          type="date"
          size="small"
          value={filterDateFrom}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          style={{ width: 145, flex: "0 0 145px" }}
        />
        <TextField
          label="Até"
          type="date"
          size="small"
          value={filterDateTo}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFilterDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          style={{ width: 145, flex: "0 0 145px" }}
        />
        {(filterUser.length > 0 || filterType || filterName.length > 0 || filterSerial || filterDateFrom || filterDateTo) && (
          <Button
            size="small"
            onClick={() => {
              setFilterUser([]);
              setFilterType("");
              setFilterName([]);
              setFilterSerial("");
              setFilterDateFrom("");
              setFilterDateTo("");
            }}
          >
            Limpar filtros
          </Button>
        )}
        <Typography variant="caption" color="textSecondary" style={{ marginLeft: "auto", textAlign: "right" }}>
          {filteredReadings.length} leituras
        </Typography>
      </Paper>

      <GridContainer>
        {/*@ts-ignore*/}
        <GridItem xs={12} sm={12} md={12}>
          <Card>
            <CardHeader color="primary">
              <h4 className={classes.cardTitleWhite}>
                {intl.get("statistics.reads")}
              </h4>
            </CardHeader>
            <CardBody>{table}</CardBody>
          </Card>
        </GridItem>
        {/* Gráficos removidos por solicitação ICL */}
      </GridContainer>
      <RecalculateReadingsModal
        open={recalculateModalOpen}
        onClose={() => setRecalculateModalOpen(false)}
        selectedReadings={selectedReadings}/>
    </div>
  );
}
